import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Device as TwilioDevice } from '@twilio/voice-sdk';
import { useAuth } from '../../contexts/AuthContext';
import { WorkspaceMergeProvider, useWorkspaceMerge } from '../../contexts/WorkspaceMergeContext';
import { api, supabase } from '../../lib/api';
import { Button, Card, Input, Badge, getLeadTypeBadge, getChannelBadge } from '../../components/ui/Kit';
import { scriptTypeForLeadChannel } from '../../data/scripts';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import Spinner from '../../components/ui/Spinner';
import ScriptPanel from '../../components/Workspace/ScriptPanel';
import ObjectionPanel from '../../components/Workspace/ObjectionPanel';
import RecentActivityPanel from '../../components/Workspace/RecentActivityPanel';
import CoverageCalculator from '../../components/Workspace/widgets/CoverageCalculator';
import PostCallModal from '../../components/Dialer/PostCallModal';
import Modal from '../../components/ui/Modal';
import BookAppointmentModal from '../../components/Appointments/BookAppointmentModal';
import * as FiIcons from 'react-icons/fi';

const {
  FiSearch, FiX, FiPhone, FiPhoneOff, FiMicOff, FiMic,
  FiChevronLeft, FiChevronRight,
  FiUserPlus, FiCalendar,
} = FiIcons;

const DEFAULT_DIALER = 'twilio';

function formatMmSs(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function dialableTwilioDestination(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

function dialableTelnyxDestination(raw) {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `1${digits}`;
  return digits;
}

const STATUS_LABEL = {
  idle: 'Idle',
  fetching_token: 'Initializing…',
  registering: 'Connecting…',
  ready: 'Ready',
  connecting: 'Dialing…',
  ringing: 'Ringing…',
  connected: 'Connected',
  ended: 'Ended',
  error: 'Error',
};

const STATUS_DOT_COLOR = {
  idle: 'bg-gray-400',
  fetching_token: 'bg-yellow-400 animate-pulse',
  registering: 'bg-yellow-400 animate-pulse',
  ready: 'bg-green-500',
  connecting: 'bg-yellow-400 animate-pulse',
  ringing: 'bg-yellow-400 animate-pulse',
  connected: 'bg-green-500 animate-pulse',
  ended: 'bg-gray-400',
  error: 'bg-red-500',
};


const WORKSPACE_SPLIT_KEY = 'workspace_column_split';
const WORKSPACE_SPLIT_DEFAULT = 68;
const WORKSPACE_SPLIT_MIN = 40;
const WORKSPACE_SPLIT_MAX = 80;
const SPLIT_HANDLE_WIDTH = 6;

function readWorkspaceSplit() {
  try {
    const raw = localStorage.getItem(WORKSPACE_SPLIT_KEY);
    if (raw != null) {
      const n = parseFloat(raw);
      if (!Number.isNaN(n)) {
        return Math.min(WORKSPACE_SPLIT_MAX, Math.max(WORKSPACE_SPLIT_MIN, n));
      }
    }
  } catch {
    /* ignore */
  }
  return WORKSPACE_SPLIT_DEFAULT;
}

/* -----------------------------------------------------------------------
 * Lead Selector section
 * ---------------------------------------------------------------------*/

function LeadSelector({ selectedLead, onSelect, onClear, leadNav, onBookAppointment }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const { addToast } = useToast();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLeads = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const allLeads = await api.leads.getAll();
      const term = searchTerm.toLowerCase();
      const filtered = (allLeads || []).filter((lead) => {
        const name = `${lead.first_name || ''} ${lead.last_name || ''}`.toLowerCase();
        const phone = (lead.phone || '').replace(/\D/g, '');
        const searchDigits = term.replace(/\D/g, '');
        return name.includes(term) || (searchDigits && phone.includes(searchDigits));
      });
      setResults(filtered.slice(0, 8));
    } catch (err) {
      console.error('[workspace] lead search failed', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLeads(val), 300);
  };

  const handleSelectLead = (lead) => {
    onSelect(lead);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    setShowCreateForm(false);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!createForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!createForm.phone.trim()) errors.phone = 'Phone is required';
    setCreateErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    try {
      const newLead = await api.leads.create({
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        phone: createForm.phone.trim(),
        lead_type: 'fresh',
        lead_source: 'manual',
      });
      onSelect(newLead);
      setCreateForm({ first_name: '', last_name: '', phone: '' });
      setCreateErrors({});
      setShowCreateForm(false);
      setQuery('');
      setResults([]);
      setShowDropdown(false);
      addToast({ type: 'success', title: 'Lead created and selected' });
    } catch (err) {
      console.error('[workspace] lead creation failed', err);
      addToast({ type: 'error', title: 'Failed to create lead' });
    } finally {
      setCreating(false);
    }
  };

  if (selectedLead) {
    return (
      <Card className="p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-sm shrink-0">
              {(selectedLead.first_name || 'L').charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {selectedLead.first_name} {selectedLead.last_name}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <span className="text-xs text-gray-500">{selectedLead.phone || '—'}</span>
                {selectedLead.email && (
                  <span className="text-xs text-gray-400">{selectedLead.email}</span>
                )}
              </div>
            </div>
            {getChannelBadge(selectedLead.channel)}
            {selectedLead.lead_type && (
              <div className="shrink-0">{getLeadTypeBadge(selectedLead.lead_type)}</div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {leadNav && (
              <div className="flex items-center gap-1 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={leadNav.onPrev}
                  disabled={!leadNav.hasPrev}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous lead"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-700 px-1 select-none whitespace-nowrap">
                  {leadNav.position} of {leadNav.total}
                </span>
                <button
                  type="button"
                  onClick={leadNav.onNext}
                  disabled={!leadNav.hasNext}
                  className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next lead"
                >
                  <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
                </button>
              </div>
            )}
            {onBookAppointment && (
              <Button size="sm" variant="primary" onClick={onBookAppointment} className="shrink-0">
                <SafeIcon icon={FiCalendar} className="mr-1.5 h-3.5 w-3.5" />
                Book Appointment
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onClear}>
              <SafeIcon icon={FiX} className="mr-1.5 h-3.5 w-3.5" />
              Change
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Card className="p-4">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.trim() && setShowDropdown(true)}
            placeholder="Search lead by name or phone number…"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Spinner className="h-4 w-4 text-primary" />
            </div>
          )}
        </div>
      </Card>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((lead) => (
            <button
              key={lead.id}
              type="button"
              onClick={() => handleSelectLead(lead)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <p className="text-sm font-medium text-gray-900">
                {lead.first_name} {lead.last_name}
              </p>
              <p className="text-xs text-gray-500">{lead.phone || '—'}</p>
            </button>
          ))}
        </div>
      )}
      {showDropdown && query.trim() && !loading && results.length === 0 && !showCreateForm && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
          <p className="text-sm text-gray-500 mb-2">No leads found</p>
          <button
            type="button"
            onClick={() => {
              setShowCreateForm(true);
              setShowDropdown(false);
              setCreateForm((prev) => ({ ...prev, first_name: query.trim() }));
            }}
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            <SafeIcon icon={FiUserPlus} className="h-3.5 w-3.5" />
            Create new lead
          </button>
        </div>
      )}
      {showCreateForm && (
        <Card className="mt-2 p-4">
          <form onSubmit={handleCreateSubmit} className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <SafeIcon icon={FiUserPlus} className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-gray-800">Create New Lead</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <input
                  type="text"
                  value={createForm.first_name}
                  onChange={(e) => {
                    setCreateForm((prev) => ({ ...prev, first_name: e.target.value }));
                    if (createErrors.first_name) setCreateErrors((prev) => ({ ...prev, first_name: undefined }));
                  }}
                  placeholder="First Name *"
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${createErrors.first_name ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                {createErrors.first_name && <p className="text-xs text-red-500 mt-1">{createErrors.first_name}</p>}
              </div>
              <div>
                <input
                  type="text"
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={createForm.phone}
                  onChange={(e) => {
                    setCreateForm((prev) => ({ ...prev, phone: e.target.value }));
                    if (createErrors.phone) setCreateErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  placeholder="Phone *"
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none ${createErrors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                />
                {createErrors.phone && <p className="text-xs text-red-500 mt-1">{createErrors.phone}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" type="submit" disabled={creating}>
                {creating ? <><Spinner className="mr-1.5 h-3.5 w-3.5" />Creating…</> : 'Create & Select'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateForm({ first_name: '', last_name: '', phone: '' });
                  setCreateErrors({});
                }}
                disabled={creating}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Dialer Bar section
 * ---------------------------------------------------------------------*/

function DialerBar({ selectedLead, onCallEnded }) {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [phase, setPhase] = useState('idle');
  const [uiStatus, setUiStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [destination, setDestination] = useState('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [dialer, setDialer] = useState(DEFAULT_DIALER);
  const [muted, setMuted] = useState(false);
  const [callerId, setCallerId] = useState('');

  const clientRef = useRef(null);
  const activeCallRef = useRef(null);
  const wasConnectedRef = useRef(false);
  const timerRef = useRef(null);
  const finalizedRef = useRef(false);
  const profileRef = useRef(null);
  const durationStartRef = useRef(null);
  const dialerRef = useRef(DEFAULT_DIALER);
  const twilioTerminationReasonRef = useRef(null);
  const onCallEndedRef = useRef(onCallEnded);
  onCallEndedRef.current = onCallEnded;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const teardownClient = useCallback(() => {
    stopTimer();
    const activeDialer = dialerRef.current;
    const call = activeCallRef.current;
    if (call) {
      try {
        if (activeDialer === 'twilio') call.disconnect?.();
        else call.hangup?.();
      } catch { /* ignore */ }
    }
    activeCallRef.current = null;
    const client = clientRef.current;
    if (client) {
      try {
        if (activeDialer === 'twilio') client.destroy?.();
        else client.disconnect?.();
      } catch { /* ignore */ }
    }
    clientRef.current = null;
  }, [stopTimer]);

  const beginConnectedTicker = useCallback(() => {
    wasConnectedRef.current = true;
    setUiStatus('connected');
    if (!durationStartRef.current) {
      durationStartRef.current = Date.now();
    }
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        if (durationStartRef.current) {
          setElapsedSec(Math.floor((Date.now() - durationStartRef.current) / 1000));
        }
      }, 500);
    }
  }, []);

  const finalizeCall = useCallback((call) => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    stopTimer();

    const hadConnection = wasConnectedRef.current;
    const durationSeconds = durationStartRef.current
      ? Math.max(0, Math.floor((Date.now() - durationStartRef.current) / 1000))
      : 0;
    durationStartRef.current = null;

    setUiStatus('ended');
    setPhase('ready');

    if (hadConnection && durationSeconds > 0) {
      onCallEndedRef.current?.({ durationSeconds, dialer: dialerRef.current });
    }

    setTimeout(() => {
      if (finalizedRef.current) {
        setUiStatus('ready');
      }
    }, 2000);
  }, [stopTimer]);

  useEffect(() => {
    if (selectedLead?.phone) {
      const activeDialer = dialerRef.current;
      setDestination(
        activeDialer === 'twilio'
          ? dialableTwilioDestination(selectedLead.phone)
          : dialableTelnyxDestination(selectedLead.phone),
      );
    } else {
      setDestination('');
    }
  }, [selectedLead]);

  useEffect(() => {
    if (!supabase) {
      setPhase('error');
      setUiStatus('error');
      setErrorMsg('Supabase not configured');
      return;
    }

    let cancelled = false;

    (async () => {
      setPhase('fetching_token');
      setUiStatus('fetching_token');
      setErrorMsg('');

      try {
        const profile = await api.auth.getCurrentUser();
        if (cancelled) return;
        profileRef.current = profile;

        const chosen =
          profile?.preferred_dialer === 'telnyx' || profile?.preferred_dialer === 'twilio'
            ? profile.preferred_dialer
            : DEFAULT_DIALER;
        setDialer(chosen);
        dialerRef.current = chosen;

        if (selectedLead?.phone) {
          setDestination(
            chosen === 'twilio'
              ? dialableTwilioDestination(selectedLead.phone)
              : dialableTelnyxDestination(selectedLead.phone),
          );
        }

        if (chosen === 'twilio') {
          await initTwilio(cancelled, profile);
        } else {
          await initTelnyx(cancelled, profile);
        }
      } catch (e) {
        if (!cancelled) {
          setPhase('error');
          setUiStatus('error');
          setErrorMsg(e?.message || 'Could not start dialer');
        }
      }
    })();

    async function initTelnyx(cancelled, profile) {
      const { token, caller_id_number } = await api.telnyx.getWebRtcToken();
      if (cancelled) return;
      setCallerId(caller_id_number || '');

      const client = new TelnyxRTC({ login_token: token });
      client.remoteElement = 'workspace-remote-audio';
      clientRef.current = client;

      client.on('telnyx.error', (err) => {
        setErrorMsg(err?.error?.message || err?.message || 'Telnyx error');
        setUiStatus('error');
      });

      client.on('telnyx.ready', () => {
        if (!cancelled) {
          setPhase('ready');
          setUiStatus('ready');
        }
      });

      client.on('telnyx.notification', (notification) => {
        const call = notification.call;
        if (notification.type === 'callUpdate' && call) {
          activeCallRef.current = call;
          const s = call.state;
          if (s === 7 || s === 8) {
            beginConnectedTicker();
          } else if (s === 4 || s === 5 || s === 6) {
            setUiStatus('ringing');
          } else if (s === 9 || s === 10 || s === 11) {
            finalizeCall(call);
          } else if (s === 0 || s === 1 || s === 2 || s === 3) {
            setUiStatus('connecting');
          }
        }
      });

      setPhase('registering');
      setUiStatus('registering');
      client.connect();
      void profile;
    }

    async function initTwilio(cancelled, profile) {
      const tokenResponse = await api.telnyx.getTwilioToken();
      if (cancelled) return;
      const { token, caller_id_number } = tokenResponse ?? {};
      if (!token) {
        setPhase('error');
        setUiStatus('error');
        setErrorMsg('twilio-token returned no token');
        return;
      }
      setCallerId(caller_id_number || '');

      const device = new TwilioDevice(token, {
        edge: 'ashburn',
        logLevel: 'warn',
        allowIncomingWhileBusy: false,
      });
      clientRef.current = device;

      device.on('error', (err) => {
        const code = err?.code;
        const msg = err?.message || 'Twilio error';
        setErrorMsg(code ? `Twilio ${code}: ${msg}` : msg);
        setUiStatus('error');
      });

      device.on('registered', () => {
        if (!cancelled) {
          setPhase('ready');
          setUiStatus('ready');
        }
      });

      device.on('incoming', (call) => {
        activeCallRef.current = call;
        wireCallEvents(call);
        try { call.accept(); } catch { /* ignore */ }
      });

      setPhase('registering');
      setUiStatus('registering');
      try {
        await device.register();
      } catch (err) {
        setErrorMsg(err?.message || 'Could not register Twilio device');
        setUiStatus('error');
        setPhase('error');
      }
      void profile;
    }

    function wireCallEvents(call) {
      call.on('ringing', () => setUiStatus('ringing'));
      call.on('accept', () => beginConnectedTicker());
      call.on('disconnect', () => {
        if (!twilioTerminationReasonRef.current) twilioTerminationReasonRef.current = 'disconnect';
        finalizeCall(call);
      });
      call.on('cancel', () => {
        twilioTerminationReasonRef.current = 'cancel';
        finalizeCall(call);
      });
      call.on('reject', () => {
        twilioTerminationReasonRef.current = 'reject';
        finalizeCall(call);
      });
      call.on('error', (err) => {
        twilioTerminationReasonRef.current = err?.message || 'error';
        setErrorMsg(err?.message || 'Call failed');
        setUiStatus('error');
      });
    }

    return () => {
      cancelled = true;
      teardownClient();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCall = async () => {
    const profile = profileRef.current;
    const client = clientRef.current;
    const activeDialer = dialerRef.current;
    if (!client || !profile?.agency_id) return;

    const dest =
      activeDialer === 'twilio'
        ? dialableTwilioDestination(destination)
        : dialableTelnyxDestination(destination);
    if (!dest) return;

    setElapsedSec(0);
    durationStartRef.current = null;
    wasConnectedRef.current = false;
    finalizedRef.current = false;
    twilioTerminationReasonRef.current = null;
    setMuted(false);
    setUiStatus('connecting');

    if (activeDialer === 'twilio') {
      try {
        const params = {
          To: dest,
          From: callerId || '',
          LeadId: selectedLead?.id ?? '',
          AgentId: profile.id ?? '',
          AgencyId: profile.agency_id ?? '',
        };
        const call = await client.connect({ params });
        activeCallRef.current = call;
        call.on('ringing', () => setUiStatus('ringing'));
        call.on('accept', () => beginConnectedTicker());
        call.on('disconnect', () => {
          if (!twilioTerminationReasonRef.current) twilioTerminationReasonRef.current = 'disconnect';
          finalizeCall(call);
        });
        call.on('cancel', () => { twilioTerminationReasonRef.current = 'cancel'; finalizeCall(call); });
        call.on('reject', () => { twilioTerminationReasonRef.current = 'reject'; finalizeCall(call); });
        call.on('error', (err) => {
          setErrorMsg(err?.message || 'Call failed');
          setUiStatus('error');
        });
      } catch (e) {
        setErrorMsg(e?.message || 'Failed to place call');
        setUiStatus('error');
      }
    } else {
      try {
        const clientState = btoa(JSON.stringify({
          lead_id: selectedLead?.id,
          agent_id: profile.id,
          agency_id: profile.agency_id,
        }));
        client.newCall({
          destinationNumber: dest,
          callerNumber: callerId || undefined,
          clientState,
          remoteElement: 'workspace-remote-audio',
          audio: true,
          video: false,
        });
      } catch (e) {
        setErrorMsg(e?.message || 'Failed to place call');
        setUiStatus('error');
      }
    }
  };

  const handleHangup = () => {
    const call = activeCallRef.current;
    const activeDialer = dialerRef.current;
    try {
      if (activeDialer === 'twilio') call?.disconnect?.();
      else call?.hangup?.();
    } catch { /* ignore */ }
    if (call) finalizeCall(call);
    else {
      stopTimer();
      durationStartRef.current = null;
      setElapsedSec(0);
      setUiStatus('ready');
      setPhase('ready');
    }
    wasConnectedRef.current = false;
  };

  const handleMuteToggle = () => {
    const call = activeCallRef.current;
    const activeDialer = dialerRef.current;
    if (!call) return;
    const newMuted = !muted;
    setMuted(newMuted);
    try {
      if (activeDialer === 'twilio') {
        call.mute(newMuted);
      } else {
        if (newMuted) call.muteAudio();
        else call.unmuteAudio();
      }
    } catch { /* ignore */ }
  };

  const busyCalling = uiStatus === 'connecting' || uiStatus === 'ringing' || uiStatus === 'connected';
  const dialerLabel = dialer === 'twilio' ? 'Twilio' : 'Telnyx';

  return (
    <Card className="p-3 shrink-0">
      <audio id="workspace-remote-audio" autoPlay playsInline className="hidden" />
      <div className="flex items-center gap-3 flex-wrap">
        {/* Destination input */}
        <div className="flex-1 min-w-[180px] max-w-xs">
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="+1…"
            disabled={busyCalling || phase === 'fetching_token' || phase === 'registering'}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none disabled:opacity-50"
          />
        </div>

        {/* Action buttons */}
        <Button
          size="sm"
          onClick={handleCall}
          disabled={!destination.trim() || phase !== 'ready' || busyCalling}
        >
          <SafeIcon icon={FiPhone} className="mr-1.5 h-3.5 w-3.5" />
          Call
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={handleHangup}
          disabled={!busyCalling}
        >
          <SafeIcon icon={FiPhoneOff} className="mr-1.5 h-3.5 w-3.5" />
          Hangup
        </Button>
        <Button
          size="sm"
          variant={muted ? 'danger' : 'outline'}
          onClick={handleMuteToggle}
          disabled={uiStatus !== 'connected'}
        >
          <SafeIcon icon={muted ? FiMicOff : FiMic} className="h-3.5 w-3.5" />
        </Button>

        {/* Status indicator */}
        <div className="flex items-center gap-2 ml-auto">
          <div className={`w-2 h-2 rounded-full ${STATUS_DOT_COLOR[uiStatus] || 'bg-gray-400'}`} />
          <span className="text-xs font-medium text-gray-600">{STATUS_LABEL[uiStatus] ?? uiStatus}</span>
        </div>

        {/* Timer */}
        <div className="text-sm font-mono font-semibold text-gray-900 tabular-nums min-w-[52px] text-right">
          {busyCalling || uiStatus === 'ended' ? formatMmSs(elapsedSec) : '00:00'}
        </div>

        {/* Provider badge */}
        <Badge variant="default" className="text-[10px]">{dialerLabel}</Badge>
      </div>

      {errorMsg && (
        <p className="text-xs text-red-600 mt-2 bg-red-50 border border-red-100 rounded px-2 py-1">
          {errorMsg}
        </p>
      )}
    </Card>
  );
}

/* -----------------------------------------------------------------------
 * Main Content Area (script + objection handler + widget tray)
 * ---------------------------------------------------------------------*/

function MainContentArea({ onBranchSelect, onScriptTypeChange, scriptType, selectedLead }) {
  const containerRef = useRef(null);
  const [splitPct, setSplitPct] = useState(readWorkspaceSplit);
  const [isDragging, setIsDragging] = useState(false);
  const [handleHovered, setHandleHovered] = useState(false);
  const splitPctRef = useRef(splitPct);
  splitPctRef.current = splitPct;

  const applySplitFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect?.width) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(WORKSPACE_SPLIT_MAX, Math.max(WORKSPACE_SPLIT_MIN, pct));
    setSplitPct(clamped);
  }, []);

  const endDrag = useCallback(() => {
    setIsDragging(false);
    try {
      localStorage.setItem(WORKSPACE_SPLIT_KEY, String(splitPctRef.current));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    const onMouseMove = (e) => applySplitFromClientX(e.clientX);
    const onMouseUp = () => endDrag();
    const onTouchMove = (e) => {
      if (e.touches[0]) applySplitFromClientX(e.touches[0].clientX);
    };
    const onTouchEnd = () => endDrag();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, applySplitFromClientX, endDrag]);

  const startDrag = (clientX) => {
    setIsDragging(true);
    applySplitFromClientX(clientX);
  };

  const rightPct = 100 - splitPct;
  const handleActive = isDragging || handleHovered;

  return (
    <div ref={containerRef} className="flex-1 flex min-h-0">
      <div
        className="overflow-y-auto rounded-lg border border-gray-200 bg-white min-h-0 shrink-0"
        style={{ width: `calc(${splitPct}% - ${SPLIT_HANDLE_WIDTH / 2}px)` }}
      >
        <ScriptPanel
          onBranchSelect={onBranchSelect}
          onScriptTypeChange={onScriptTypeChange}
          scriptType={scriptType}
        />
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(splitPct)}
        className="shrink-0 flex items-center justify-center cursor-col-resize touch-none transition-colors"
        style={{
          width: SPLIT_HANDLE_WIDTH,
          backgroundColor: handleActive ? 'rgba(0, 170, 255, 0.3)' : 'transparent',
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          startDrag(e.clientX);
        }}
        onTouchStart={(e) => {
          if (e.touches[0]) {
            e.preventDefault();
            startDrag(e.touches[0].clientX);
          }
        }}
        onMouseEnter={() => setHandleHovered(true)}
        onMouseLeave={() => setHandleHovered(false)}
      >
        <div className="flex flex-col gap-1 pointer-events-none" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span key={i} className="w-1 h-1 rounded-full bg-gray-300/90" />
          ))}
        </div>
      </div>

      <div
        className="flex flex-col min-h-0 rounded-lg border border-gray-200 bg-white overflow-hidden shrink-0"
        style={{ width: `calc(${rightPct}% - ${SPLIT_HANDLE_WIDTH / 2}px)` }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ObjectionPanel />
        </div>
        <RecentActivityPanel leadId={selectedLead?.id} />
      </div>
    </div>
  );
}

function WidgetTray() {
  return (
    <div className="shrink-0 overflow-x-auto" style={{ height: 220 }}>
      <div className="min-w-full">
        <CoverageCalculator />
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Auto-save helpers
 * ---------------------------------------------------------------------*/

function parseNum(raw) {
  if (raw == null || raw === '') return 0;
  const cleaned = String(raw).replace(/[$,]/g, '');
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

function computeCoverage(intake_data) {
  const mortgageBalance = parseNum(intake_data.mortgageBalance);
  const carLoans = parseNum(intake_data.carLoans);
  const creditCards = parseNum(intake_data.creditCards);
  const otherDebt = parseNum(intake_data.otherDebt);
  const totalDebt = mortgageBalance + carLoans + creditCards + otherDebt;

  const annualIncome = parseNum(intake_data.annualIncome);
  const incomeYears = parseNum(intake_data.incomeYears);
  const incomeReplacement = parseNum(intake_data.incomeReplacement) || (annualIncome * (incomeYears || 10));

  const numberOfKids = parseNum(intake_data.numberOfKids);
  const educationPerChild = parseNum(intake_data.educationPerChild);
  const educationTotal = parseNum(intake_data.educationTotal) || (numberOfKids * (educationPerChild || 50000));

  const fexCoverageAmount = parseNum(intake_data.fexCoverageAmount);
  const isFexPath = fexCoverageAmount > 0 && totalDebt === 0 && incomeReplacement === 0;
  const totalInsurableNeed = isFexPath ? fexCoverageAmount : totalDebt + incomeReplacement + educationTotal;

  return { totalDebt, incomeReplacement, educationTotal, totalInsurableNeed };
}

/* -----------------------------------------------------------------------
 * Workspace Page (inner, wrapped by provider)
 * ---------------------------------------------------------------------*/

function buildSessionNotes(scriptType, branchesTaken, intakeData) {
  const parts = [];
  const label = scriptType === 'fex' ? 'FEX' : 'Ethos';
  parts.push(`Script: ${label}`);

  if (branchesTaken?.length) {
    const path = branchesTaken.map((b) => b.branchLabel).join(' → ');
    parts.push(`Path: ${label} → ${path}`);
  }

  const intakeLines = [];
  if (intakeData?.beneficiaryName) intakeLines.push(`Beneficiary: ${intakeData.beneficiaryName}`);
  const coverage = computeCoverage(intakeData || {});
  if (intakeData?.fexCoverageAmount) intakeLines.push(`Coverage amount: $${Number(intakeData.fexCoverageAmount).toLocaleString()}`);
  if (coverage.totalInsurableNeed > 0) intakeLines.push(`Total insurable need: $${coverage.totalInsurableNeed.toLocaleString()}`);
  if (intakeLines.length) parts.push(intakeLines.join('\n'));

  return parts.join('\n');
}

function WorkspaceInner() {
  const { user } = useAuth();
  const { bulkSetFields, intake_data } = useWorkspaceMerge();
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [selectedLead, setSelectedLead] = useState(null);
  const [preloading, setPreloading] = useState(false);
  const [scriptType, setScriptType] = useState('ethos');

  const [navLeadIds, setNavLeadIds] = useState(location.state?.leadIds || null);
  const [navCurrentIndex, setNavCurrentIndex] = useState(location.state?.currentIndex ?? null);

  const [postCallOpen, setPostCallOpen] = useState(false);
  const [postCallDuration, setPostCallDuration] = useState(0);
  const [postCallSaving, setPostCallSaving] = useState(false);
  const [postCallDefaultNotes, setPostCallDefaultNotes] = useState('');
  const [nextLeadPromptOpen, setNextLeadPromptOpen] = useState(false);
  const [bookApptOpen, setBookApptOpen] = useState(false);

  const currentSessionIdRef = useRef(null);
  const branchesTakenRef = useRef([]);
  const lastAutoRoutedLeadIdRef = useRef(null);
  const intakeDebounceRef = useRef(null);
  const coverageDebounceRef = useRef(null);

  useEffect(() => { document.title = 'Call Workspace — Prosperity Leads'; }, []);

  useEffect(() => {
    if (!selectedLead) setBookApptOpen(false);
  }, [selectedLead]);

  // Auto-load lead from URL ?lead=:id
  useEffect(() => {
    const leadId = searchParams.get('lead');
    if (leadId && !selectedLead) {
      setPreloading(true);
      api.leads.getById(leadId)
        .then((lead) => {
          if (lead) {
            setSelectedLead(lead);
            bulkSetFields({
              firstName: lead.first_name || '',
              lastName: lead.last_name || '',
              dob: lead.dob || '',
            });
          }
        })
        .catch((err) => console.error('[workspace] failed to preload lead', err))
        .finally(() => setPreloading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate agentName from current user
  useEffect(() => {
    if (user?.full_name) {
      bulkSetFields({ agentName: user.full_name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.full_name]);

  // --- Auto-save: create session when lead is selected ---
  useEffect(() => {
    if (!selectedLead?.id) {
      currentSessionIdRef.current = null;
      branchesTakenRef.current = [];
      return;
    }

    (async () => {
      try {
        const session = await api.workspaceSessions.create({
          lead_id: selectedLead.id,
          script_type: scriptType,
        });
        currentSessionIdRef.current = session.id;
        branchesTakenRef.current = [];
      } catch (err) {
        console.error('[workspace] failed to create session', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.id]);

  // --- Auto-save: debounced intake_data save ---
  useEffect(() => {
    if (!currentSessionIdRef.current) return;
    if (intakeDebounceRef.current) clearTimeout(intakeDebounceRef.current);

    intakeDebounceRef.current = setTimeout(async () => {
      const sessionId = currentSessionIdRef.current;
      if (!sessionId) return;
      try {
        await api.workspaceSessions.update(sessionId, { intake_data });
        if (selectedLead?.id) {
          const coverageCalc = computeCoverage(intake_data);
          await api.leads.update(selectedLead.id, {
            metadata: {
              ...(selectedLead.metadata || {}),
              intake: intake_data,
              coverage: coverageCalc,
            },
          });
        }
      } catch (err) {
        console.error('[workspace] silent save intake failed', err);
      }
    }, 800);

    return () => {
      if (intakeDebounceRef.current) clearTimeout(intakeDebounceRef.current);
    };
  }, [intake_data, selectedLead]);

  // --- Auto-save: debounced coverage calculation save ---
  useEffect(() => {
    if (!currentSessionIdRef.current) return;
    if (coverageDebounceRef.current) clearTimeout(coverageDebounceRef.current);

    coverageDebounceRef.current = setTimeout(async () => {
      const sessionId = currentSessionIdRef.current;
      if (!sessionId) return;
      try {
        const coverageCalc = computeCoverage(intake_data);
        await api.workspaceSessions.update(sessionId, {
          coverage_calculation: coverageCalc,
        });
      } catch (err) {
        console.error('[workspace] silent save coverage failed', err);
      }
    }, 800);

    return () => {
      if (coverageDebounceRef.current) clearTimeout(coverageDebounceRef.current);
    };
  }, [intake_data]);

  const handleBranchSelect = useCallback(async (stepId, branchLabel) => {
    const sessionId = currentSessionIdRef.current;
    if (!sessionId) return;

    const newBranch = { stepId, branchLabel, timestamp: new Date().toISOString() };
    branchesTakenRef.current = [...branchesTakenRef.current, newBranch];

    try {
      await api.workspaceSessions.update(sessionId, {
        branches_taken: branchesTakenRef.current,
      });
    } catch (err) {
      console.error('[workspace] silent save branches failed', err);
    }
  }, []);

  const handleScriptTypeChange = useCallback((type) => {
    setScriptType(type);
  }, []);

  useEffect(() => {
    if (!selectedLead?.id) {
      lastAutoRoutedLeadIdRef.current = null;
      return;
    }
    if (lastAutoRoutedLeadIdRef.current === selectedLead.id) return;
    lastAutoRoutedLeadIdRef.current = selectedLead.id;
    const autoType = scriptTypeForLeadChannel(selectedLead.channel, { ldDefault: 'ethos' });
    setScriptType(autoType);
  }, [selectedLead?.id, selectedLead?.channel]);

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    bulkSetFields({
      firstName: lead.first_name || '',
      lastName: lead.last_name || '',
      dob: lead.dob || '',
    });
  };

  const handleClearLead = () => {
    setSelectedLead(null);
    currentSessionIdRef.current = null;
    branchesTakenRef.current = [];
    bulkSetFields({ firstName: '', lastName: '', dob: '' });
  };

  const navigateToLeadByIndex = useCallback(async (newIndex) => {
    if (!navLeadIds || newIndex < 0 || newIndex >= navLeadIds.length) return;
    const nextLeadId = navLeadIds[newIndex];
    try {
      const lead = await api.leads.getById(nextLeadId);
      if (lead) {
        setSelectedLead(lead);
        setNavCurrentIndex(newIndex);
        currentSessionIdRef.current = null;
        branchesTakenRef.current = [];
        bulkSetFields({
          firstName: lead.first_name || '',
          lastName: lead.last_name || '',
          dob: lead.dob || '',
        });
      }
    } catch (err) {
      console.error('[workspace] failed to load lead for nav', err);
    }
  }, [navLeadIds, bulkSetFields]);

  const leadNav = navLeadIds && navCurrentIndex != null && navCurrentIndex >= 0 ? {
    hasPrev: navCurrentIndex > 0,
    hasNext: navCurrentIndex < navLeadIds.length - 1,
    position: navCurrentIndex + 1,
    total: navLeadIds.length,
    onPrev: () => navigateToLeadByIndex(navCurrentIndex - 1),
    onNext: () => navigateToLeadByIndex(navCurrentIndex + 1),
  } : null;

  const handleCallEnded = useCallback(({ durationSeconds }) => {
    if (!selectedLead?.id || durationSeconds <= 0) return;
    const defaultNotes = buildSessionNotes(scriptType, branchesTakenRef.current, intake_data);
    setPostCallDuration(durationSeconds);
    setPostCallDefaultNotes(defaultNotes);
    setPostCallOpen(true);
  }, [selectedLead?.id, scriptType, intake_data]);

  const handlePostCallSave = useCallback(async ({ outcome, leadStatus, notes, roomType }) => {
    if (!selectedLead?.id) return;
    setPostCallSaving(true);
    try {
      await api.calls.log(selectedLead.id, {
        direction: 'outbound',
        outcome,
        duration_seconds: postCallDuration,
        notes: notes || null,
        room_type: roomType || null,
      });

      if (leadStatus && leadStatus !== selectedLead.status) {
        await api.leads.update(selectedLead.id, { status: leadStatus });
        setSelectedLead((prev) => prev ? { ...prev, status: leadStatus } : prev);
      }

      if (currentSessionIdRef.current) {
        try {
          await api.workspaceSessions.update(currentSessionIdRef.current, {
            updated_at: new Date().toISOString(),
          });
        } catch { /* silent */ }
      }

      addToast({ type: 'success', title: 'Call logged successfully' });
      setPostCallOpen(false);

      if (leadNav?.hasNext) {
        setNextLeadPromptOpen(true);
      }
    } catch (e) {
      console.error('[workspace] post-call save failed', e);
      addToast({ type: 'error', title: 'Could not save call log', message: e?.message });
    } finally {
      setPostCallSaving(false);
    }
  }, [selectedLead, postCallDuration, addToast, leadNav?.hasNext]);

  const handlePostCallSkip = useCallback(() => {
    setPostCallOpen(false);
    setPostCallDuration(0);
    setPostCallDefaultNotes('');
  }, []);

  const handleNextLeadAfterLog = useCallback(() => {
    setNextLeadPromptOpen(false);
    leadNav?.onNext?.();
  }, [leadNav]);

  if (preloading) {
    return (
      <div className="flex items-center justify-center h-full min-h-0">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0 overflow-hidden">
      {/* Section 1 — Lead Selector */}
      <LeadSelector
        selectedLead={selectedLead}
        onSelect={handleSelectLead}
        onClear={handleClearLead}
        leadNav={leadNav}
        onBookAppointment={selectedLead ? () => setBookApptOpen(true) : undefined}
      />

      {/* Section 2 — Dialer Bar */}
      <DialerBar selectedLead={selectedLead} onCallEnded={handleCallEnded} />

      {/* Section 3 — Script + Objection columns (never shrinks) */}
      <MainContentArea
        onBranchSelect={handleBranchSelect}
        onScriptTypeChange={handleScriptTypeChange}
        scriptType={scriptType}
        selectedLead={selectedLead}
      />

      {/* Section 4 — Widget Tray (full width) */}
      <WidgetTray />
      {/* Post-call logging modal */}
      <PostCallModal
        isOpen={postCallOpen}
        onSkip={handlePostCallSkip}
        onSave={handlePostCallSave}
        leadName={selectedLead ? `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim() : 'Lead'}
        currentLeadStatus={selectedLead?.status || 'new'}
        suggestedOutcome="answered"
        defaultNotes={postCallDefaultNotes}
        durationSeconds={postCallDuration}
        saving={postCallSaving}
      />

      <BookAppointmentModal
        isOpen={bookApptOpen}
        onClose={() => setBookApptOpen(false)}
        lead={selectedLead}
        onSuccess={() => {}}
      />

      {/* "Move to next lead?" prompt after saving */}
      <Modal isOpen={nextLeadPromptOpen} onClose={() => setNextLeadPromptOpen(false)} title="Call Logged">
        <p className="text-sm text-gray-600 mt-1 mb-5">Move to the next lead?</p>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => setNextLeadPromptOpen(false)}>
            Stay Here
          </Button>
          <Button className="flex-1" onClick={handleNextLeadAfterLog}>
            Next →
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Exported page component (wrapped in MergeProvider)
 * ---------------------------------------------------------------------*/

export default function CallWorkspace() {
  return (
    <WorkspaceMergeProvider>
      <WorkspaceInner />
    </WorkspaceMergeProvider>
  );
}
